/**
 * ndviPalette — shared NDVI colour scale + value→colour mapping.
 *
 * Единый источник NDVI-палитры (научная шкала 0.20 → 1.00) для:
 *   - MapVerticalLegend (Палитра 1) на /map,
 *   - легенды NDVI на карточке поля (AK-379),
 *   - производной раскраски «NDVI средний» (flat contour fill по mean-значению).
 *
 * Живёт в `src/components/map/` — whitelist для hex-литералов
 * (frontend-vite/CLAUDE.md: NDVI scientific colormap). Это domain-палитра,
 * НЕ semantic-токены DS.
 *
 * Sync с backend `rasters/palettes.py` (NDVI absolute LUT) — те же 13 stops.
 */

export interface NdviStop {
  /** hex-цвет для этого порога */
  color: string;
  /** нижняя граница NDVI для этого класса (значение ≥ threshold попадает сюда) */
  threshold: number;
  /** подпись для легенды */
  label: string;
}

// 13 stops, от высокого NDVI к низкому. Совпадает с PALETTE_1_STOPS в
// MapVerticalLegend.tsx (та же научная шкала). threshold — нижняя граница класса.
export const NDVI_STOPS: NdviStop[] = [
  { color: '#1a4d1f', threshold: 0.9, label: '1.00' },
  { color: '#287428', threshold: 0.8, label: '0.80' },
  { color: '#3b9c34', threshold: 0.7, label: '0.70' },
  { color: '#65bf45', threshold: 0.6, label: '0.60' },
  { color: '#86d35a', threshold: 0.55, label: '0.55' },
  { color: '#a3df72', threshold: 0.5, label: '0.50' },
  { color: '#c1e88c', threshold: 0.45, label: '0.45' },
  { color: '#e0eea4', threshold: 0.4, label: '0.40' },
  { color: '#f3e681', threshold: 0.35, label: '0.35' },
  { color: '#f4c651', threshold: 0.3, label: '0.30' },
  { color: '#e89238', threshold: 0.25, label: '0.25' },
  { color: '#d6582b', threshold: 0.2, label: '0.20' },
  { color: '#000000', threshold: -1, label: '<0.20' },
];

/**
 * NDVI_CONTRAST_STOPS — Палитра 2 (NDVI Контрастный): 7 относительных классов
 * на [0.2, 1.0] per-field stretch. Единый источник для:
 *   - MapVerticalLegend (Палитра 2) на /map,
 *   - FieldNdviLegend на карточке поля (AK-379).
 *
 * Раньше дублировалась в двух местах (PALETTE_2_STOPS / CONTRAST_STOPS) —
 * консолидировано в R4, чтобы устранить дрейф. Чёрный <0.20
 * (NDVI_BELOW_THRESHOLD_COLOR) — фиксированный hard-cutoff (почва/вода),
 * рендерится легендами отдельной строкой.
 */
export const NDVI_CONTRAST_STOPS: { color: string; label: string }[] = [
  { color: '#1a9850', label: 'значит. выше' },
  { color: '#66bd63', label: 'выше' },
  { color: '#a6d96a', label: 'чуть выше' },
  { color: '#fee08b', label: 'среднее' },
  { color: '#fdae61', label: 'чуть ниже' },
  { color: '#f46d43', label: 'ниже' },
  { color: '#d73027', label: 'значит. ниже' },
];

/** Чёрный hard-cutoff <0.20 (почва/вода), общий для NDVI-легенд. */
export const NDVI_BELOW_THRESHOLD_COLOR = '#000000';

/**
 * ndviColorForValue — вернуть hex-цвет NDVI-палитры для конкретного среднего
 * значения. Используется «NDVI средним» для flat-заливки контура поля.
 *
 * Значения ≥0.90 → тёмно-зелёный, <0.20 → чёрный (почва/вода). null → серый
 * placeholder (нет данных).
 */
export function ndviColorForValue(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '#9CA3AF';
  for (const stop of NDVI_STOPS) {
    if (value >= stop.threshold) return stop.color;
  }
  return '#000000';
}
